import { Alert, Col, Container, Form, Row, Card, Button as BsButton, OverlayTrigger, Tooltip } from "react-bootstrap";
import { InfoCircleFill } from "react-bootstrap-icons";
import { Input } from "./Controls/Input";
import { Select } from "./Controls/Select";
import { SpotSyncTable } from "./Controls/SpotSyncTable";
import { createLocalDate, createLocalTime, showBrowserDate, showBrowserTime, showDuration } from "./Data/Date";
import { cents, Money, showMoney } from "./Data/Money";
import { hours, mins } from "./Data/Duration";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { useOptionalParamIds, useParamIds } from "./Data/Common";
import { Duration, LocalDate, LocalTime } from "@js-joda/core";
import { Button } from "./Controls/Button";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { PageTitle } from "./Controls/PageTitle";
import { RateProgram, RateProgramData, RateType, ValidationType } from "./Data/ApiTypes";
import { Api, identity } from "./Data/Api";
import { loadResult } from "./Data/Result";
import { MoneyInput } from "./Controls/MoneyInput";
import { toast } from "react-toastify";

interface DurationRateForm {
    name:      string;
    startDate: string;
    startTime: string;
    endDate:   string | null;
    endTime:   string | null;
    inAfter:   string | null;
    inBefore:  string | null;
    outAfter:  string | null;
    outBefore: string | null;
    upTo:      number;
    units:     "minutes" | "hours";
    price:     number;
    isEvent:   boolean;
    isValidation: boolean;
    rateType: RateType;
    canBeValidated: boolean;
    validationType: ValidationType;
}

// Days of week enum matching C# backend
enum DaysOfWeek {
    Sunday    = 1 << 0,  // 1
    Monday    = 1 << 1,  // 2
    Tuesday   = 1 << 2,  // 4
    Wednesday = 1 << 3,  // 8
    Thursday  = 1 << 4,  // 16
    Friday    = 1 << 5,  // 32
    Saturday  = 1 << 6,  // 64
}

const ALL_DAYS = [
    DaysOfWeek.Sunday,
    DaysOfWeek.Monday,
    DaysOfWeek.Tuesday,
    DaysOfWeek.Wednesday,
    DaysOfWeek.Thursday,
    DaysOfWeek.Friday,
    DaysOfWeek.Saturday,
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Convert bit value to array of selected days
function decodeDays(bitValue: number | null): DaysOfWeek[] {
    if (bitValue === null) return [...ALL_DAYS];
    return ALL_DAYS.filter(day => (bitValue & day) === day);
}

// Convert array of selected days to bit value (or null if all days)
function encodeDays(selectedDays: DaysOfWeek[]): number | null {
    if (selectedDays.length === ALL_DAYS.length) return null;
    return selectedDays.reduce((acc, day) => acc | day, 0);
}

interface RateEntryKeyless {
    upTo: Duration;
    rate: Money;
}

function canShowValidationDropdown(rateType: RateType) {
  return rateType === RateType.Hourly || rateType === RateType.Event;
}

function canShowValidationTypeDropdown(rateType: RateType) {
  return rateType === RateType.Validation;
}

function getBackendFlags(rateType: RateType) {
    return {
        isEvent: rateType === RateType.Event,
        isValidation: rateType === RateType.Validation
    };
}

function getTabFromRateType(rateType: RateType): string {
    switch (rateType) {
        case RateType.Event: return 'event';
        case RateType.Validation: return 'validation';
        default: return 'hourly';
    }
}

export function FacilityRateDurationPage() {
    const { facilityId } = useParamIds();
    const { rateId     } = useOptionalParamIds();
    const prg = useLoaderData() as RateProgram;
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Determine initial rate type based on tab parameter
    const tabParam = searchParams.get('tab');
    const getInitialRateType = () => {
        if (tabParam === 'event') return RateType.Event;
        if (tabParam === 'validation') return RateType.Validation;
        return RateType.Hourly;
    };

    const initialRateType = getInitialRateType();
    const { isEvent, isValidation } = getBackendFlags(initialRateType);

    let initForm: DurationRateForm = {
        units:     "minutes",
        upTo:      0,
        price:     1,
        name:      "",
        startDate: showBrowserDate( LocalDate.now() ),
        startTime: showBrowserTime( LocalTime.now() ),
        endDate:   null,
        endTime:   null,
        inAfter:   null,
        inBefore:  null,
        outAfter:  null,
        outBefore: null,
        isEvent:   isEvent,
        isValidation: isValidation,
        rateType: initialRateType,
        canBeValidated: false,
        validationType: ValidationType.Fixed
    };
    let initRates: RateEntryKeyless[] = [];
    let verb = "Create";
    if( rateId !== undefined ) {
        verb      = "Edit";
        initRates = [...prg.entries];
        initForm = {
            units:     "hours",
            upTo:      0,
            price:     1,
            name:      prg.name,
            startDate: showBrowserDate( prg.start.toLocalDate() ),
            startTime: showBrowserTime( prg.start.toLocalTime() ),
            endDate:   prg.end ? showBrowserDate( prg.end.toLocalDate() ) : null,
            endTime:   prg.end ? showBrowserTime( prg.end.toLocalTime() ) : null,
            inAfter:   prg.inAfter   ? showBrowserTime( prg.inAfter   ) : null,
            inBefore:  prg.inBefore  ? showBrowserTime( prg.inBefore  ) : null,
            outAfter:  prg.outAfter  ? showBrowserTime( prg.outAfter  ) : null,
            outBefore: prg.outBefore ? showBrowserTime( prg.outBefore ) : null,
            isEvent:   prg.isEvent,
            isValidation: prg.isValidation,
            rateType: prg.isValidation ? RateType.Validation : prg.isEvent ? RateType.Event : RateType.Hourly,
            canBeValidated: prg.canBeValidated,
            validationType: prg.validationType
        };
    }

    const [ msg,   setMsg   ] = useState<string>();
    const [ rates, setRates ] = useState<RateEntryKeyless[]>( initRates );
    const [ inputKey, setInputKey ] = useState(0);
    const [ selectedDays, setSelectedDays ] = useState<DaysOfWeek[]>( 
        rateId !== undefined ? decodeDays(prg.applicableDays) : [...ALL_DAYS] 
    );

    // Any time of day = no in/out time restrictions. When true, the four time inputs are cleared and disabled.
    const hasTimeRestrictions = rateId !== undefined && !!(initForm.inAfter || initForm.inBefore || initForm.outAfter || initForm.outBefore);
    const [ anyTimeOfDay, setAnyTimeOfDay ] = useState(!hasTimeRestrictions);

    //form handling
    const frm = useForm<DurationRateForm>( { defaultValues: initForm } );
    const { register, handleSubmit, watch, setValue, setError, formState: { errors }, clearErrors } = { ...frm };
    const vals = watch(); //watch entire form

    const isDiscountValidationType = watch('validationType') == ValidationType.Discount;

    const isFlatRate = vals.isEvent || vals.isValidation;

    // Toggle day selection
    const toggleDay = (day: DaysOfWeek) => {
        setSelectedDays(prev => {
            if (prev.includes(day)) {
                // Don't allow deselecting all days
                if (prev.length === 1) return prev;
                return prev.filter(d => d !== day);
            } else {
                return [...prev, day].sort((a, b) => ALL_DAYS.indexOf(a) - ALL_DAYS.indexOf(b));
            }
        });
    };

    const toggleAllDays = () => {
        if (selectedDays.length === ALL_DAYS.length) {
            // Don't allow deselecting all
            return;
        } else {
            setSelectedDays([...ALL_DAYS]);
        }
    };

    // Clear message when validation conditions are met
    useEffect(() => {
        if (vals.name?.trim() && rates.length > 0) {
            setMsg(undefined);
        }
    }, [vals.name, rates.length]);

    function addRateEntry(): void {
        const toDur = vals.units === "hours" ? hours : mins;
        if( vals.upTo <= 0 && !isFlatRate) {
            setError( "upTo", { type: "min", message: "Up To must be greater than 0" } );
            return;
        }
        if (isFlatRate && rates.length > 0) {
            toast.error("This rate type can only have one rate entry");
            return;
        }
        if (vals.price <= 0) {
            setError( "price", { type: "min", message: "Price must be greater than 0" } );
            return;
        }
        const findRes = rates.find( r => r.upTo.equals( toDur( vals.upTo ) ) )
        if( findRes ) {
            setError( "upTo", { type: "validate", message: "There is already an entry with that value" } );
            return;
        }
        setRates( [...rates, { upTo: toDur( vals.upTo ), rate: cents( vals.price ) }] );
        setValue( "upTo",  0 );
        setValue( "price", 0 );

        // Hack to increment inputKey to force MoneyInput to completely re-render with the new value
        // This is necessary because MoneyInput maintains its own internal state
        // and doesn't always update its display value when the form value changes
        setInputKey(prev => prev + 1);
        clearErrors(["upTo", "units", "price"]);
    }

    function deleteRate( el: RateEntryKeyless ): void {
        setRates( rates.filter( r => r !== el ) );
        clearErrors(["upTo", "units", "price"]);
    }
    

    const onSubmit = async ( data: DurationRateForm ) => {
        if (!data.name.trim()) {
            setMsg("Rate name is required");
            return;
        }
        if (rates.length === 0) {
            setMsg("At least one rate entry is required");
            return;
        }
       
        setIsSubmitting(true);
        try {
            const req: RateProgramData = {
                facilityId: facilityId,
                name:       data.name,
                start:      createLocalDate( data.startDate ).atTime( createLocalTime( data.startTime ) ),
                end:        data.endDate && data.endTime ? createLocalDate( data.endDate ).atTime( createLocalTime( data.endTime ) ) : null,
                inAfter:    anyTimeOfDay ? null : (data.inAfter   ? createLocalTime( data.inAfter   ) : null),
                inBefore:   anyTimeOfDay ? null : (data.inBefore  ? createLocalTime( data.inBefore  ) : null),
                outAfter:   anyTimeOfDay ? null : (data.outAfter  ? createLocalTime( data.outAfter  ) : null),
                outBefore:  anyTimeOfDay ? null : (data.outBefore ? createLocalTime( data.outBefore ) : null),
                entries:    rates,
                isEvent:    data.isEvent,
                isValidation: data.isValidation,
                applicableDays: encodeDays(selectedDays),
                canBeValidated: data.canBeValidated,
                validationType: data.validationType
            }
            let res: RateProgram | string;
            if( !rateId ) {
                res = loadResult( await Api.facilityRateCreate( facilityId!, req ), identity, identity );
            } else {
                res = loadResult( await Api.facilityRateUpdate( facilityId!, rateId!, req ), identity, identity );
            }
            if( res instanceof String ) {
                setMsg( res as string );
            } else {
                const currentTab = getTabFromRateType(data.rateType);
                nav(`/facility/${facilityId}/rate?tab=${currentTab}`);
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    const localDate = showBrowserDate( LocalDate.now() );

    return <Container fluid>
        <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="justify-content-left">
                <Col lg="8" sm="12">
                     <div className="d-flex align-items-center gap-3">
                        <BsButton variant="link" className="p-0" onClick={() => {
                            const currentTab = getTabFromRateType(vals.rateType);
                            nav(`/facility/${facilityId}/rate?tab=${currentTab}`);
                        }}>
                            ← Back
                        </BsButton>  
                        <PageTitle className="mb-0">
                            {verb} Rate
                        </PageTitle>
                    </div>    
                    <Card className="mb-3">
                        <Card.Body>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
                                gridAutoRows: "auto",
                                gap: "0.5rem",
                            }}>
                                <div style={{ gridColumn: "1 / span 3", gridRow: 1 }}>
                                    <Input
                                        type="text"
                                        label="Name"
                                        explanation="This name will be shown to parkers on SpotSync"
                                        min={localDate}
                                        {...register("name")}
                                    />
                                </div>

                                <div style={{ gridColumn: 4, gridRow: 1 }}>
                        <Form.Group controlId="rateType">
                        <Form.Label>Rate Type</Form.Label>
                        <Form.Select
                            disabled={rates.length > 0}
                            title={rates.length > 0 ? "Remove all rate entries to change rate type" : ""}
                            value={watch("rateType")} // controlled by react-hook-form
                            onChange={(e) => {
                            const selected = Number(e.target.value) as RateType;
                            setValue("rateType", selected);
                            const { isEvent, isValidation } = getBackendFlags(selected);
                            setValue("isEvent", isEvent);
                            setValue("isValidation", isValidation);
                            }}
                        >
                            {Object.keys(RateType)
                            .filter((k) => isNaN(Number(k)))
                            .map((key) => (
                                <option key={key} value={RateType[key as keyof typeof RateType]}>
                                {key}
                                </option>
                            ))}
                        </Form.Select>
                        </Form.Group>
                    </div>

                    {canShowValidationDropdown(watch("rateType")) && (
                        <div style={{ gridColumn: 5, gridRow: 1 }}>
                            <Form.Group controlId="canBeValidated">
                            <Form.Label>
                                Can be validated?{' '}
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="can-be-validated-tooltip">
                                            When enabled, this rate can be discounted through validation
                                        </Tooltip>
                                    }
                                >
                                    <InfoCircleFill 
                                        className="icon-shift-up"
                                        size={14} 
                                        style={{ cursor: 'help', marginLeft: '4px' }} 
                                    />
                                </OverlayTrigger>
                            </Form.Label>
                            
                            <Form.Select
                                value={watch("canBeValidated") ? "true" : "false"}
                                onChange={(e) => {
                                const val = e.target.value === "true";
                                setValue("canBeValidated", val, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                                }}
                            >
                                <option value="false">No</option>
                                <option value="true">Yes</option>
                            </Form.Select>
                            </Form.Group>
                        </div>
                    )}

                    {canShowValidationTypeDropdown(watch("rateType")) && (
                        <div style={{ gridColumn: 5, gridRow: 1 }}>
                        <Form.Group controlId="validationType">
                            <Form.Label>
                                Validation Type
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="validation-type-tooltip">
                                           Fixed applies a flat rate. Discount reduces the parker’s rate by a set amount
                                        </Tooltip>
                                    }
                                >
                                    <InfoCircleFill 
                                        className="icon-shift-up"
                                        size={14} 
                                        style={{ cursor: 'help', marginLeft: '4px' }} 
                                    />
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Select
                                {...register("validationType", {
                                    setValueAs: (v) => Number(v)
                                })}
                                defaultValue={0}
                            >
                                <option value={0}>Fixed</option>
                                <option value={1}>Discount</option>
                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Body>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                gridAutoRows: "auto",
                                gap: "0.5rem",
                            }}>
                                <div style={{ gridColumn: 1, gridRow: 1 }}>
                                    <Input
                                        type="date"
                                        label="Start Date"
                                        className="mb-0"
                                        min={rateId === undefined ? localDate : undefined}
                                        {...register("startDate", { required: "Start Date is required" })}
                                        error={errors.startDate}
                                    />
                                </div>
                                <div style={{ gridColumn: 2, gridRow: 1 }}>
                                    <Input
                                        type="time"
                                        label="Start Time"
                                        className="mb-0"
                                        {...register("startTime", { required: "Start Time is required" })}
                                        error={errors.startTime}
                                    />
                                </div>
                                <div style={{ gridColumn: 3, gridRow: 1 }}>
                                    <Input type="date" label="End Date" className="mb-1" {...register("endDate")} error={errors.endDate} />
                                </div>
                                <div style={{ gridColumn: 4, gridRow: 1 }}>
                                    <Input type="time" label="End Time" className="mb-1" {...register("endTime")} error={errors.endTime} />
                                </div>
                                <div className="mb-0" style={{ gridColumn: "1 / -1", gridRow: 2, marginTop: "-10px", paddingTop: "0px" }}>
                                    <Form.Text className="text-muted">The date range the rate program should be applied.</Form.Text>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {!isFlatRate && (
                        <Card className="mb-3">
                            <Card.Body>
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                    gridAutoRows: "auto",
                                    gap: "0.5rem",
                                }}>
                                    <div style={{ gridColumn: "1 / -1", gridRow: 1 }} className="mb-2">
                                        <Form.Check 
                                            type="switch"
                                            id="anyTimeOfDay"
                                            label="Rates should apply any time of day"
                                            checked={anyTimeOfDay}
                                            onChange={(e) => setAnyTimeOfDay(e.target.checked)}
                                        />
                                        <Form.Text className="text-muted">
                                            {anyTimeOfDay ? "Rate applies regardless of entry/exit time." : "Optionally restrict by in/out times below."}
                                        </Form.Text>
                                    </div>
                                    <div style={{ gridColumn: 1, gridRow: 2 }}>
                                        <Input type="time" label="In After"
                                            disabled={anyTimeOfDay}
                                            {...register("inAfter")} error={errors.inAfter} />
                                    </div>
                                    <div style={{ gridColumn: 2, gridRow: 2 }}>
                                        <Input type="time" label="In Before"
                                            disabled={anyTimeOfDay}
                                            {...register("inBefore")} error={errors.inBefore} />
                                    </div>
                                    <div style={{ gridColumn: 3, gridRow: 2 }}>
                                        <Input type="time" label="Out After"
                                            disabled={anyTimeOfDay}
                                            {...register("outAfter")} error={errors.outAfter} />
                                    </div>
                                    <div style={{ gridColumn: 4, gridRow: 2 }}>
                                        <Input type="time" label="Out Before"
                                            disabled={anyTimeOfDay}
                                            {...register("outBefore")} error={errors.outBefore} />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <Form.Label >Applicable Days</Form.Label>
                                    <div className="d-flex gap-2 flex-wrap align-items-center">
                                        {ALL_DAYS.map((day, index) => (
                                            <Form.Check
                                                key={day}
                                                type="checkbox"
                                                id={`day-${day}`}
                                                label={DAY_NAMES[index]}
                                                checked={selectedDays.includes(day)}
                                                onChange={() => toggleDay(day)}
                                                style={{ minWidth: '60px' }}
                                            />
                                        ))}
                                        <BsButton
                                            variant="link"
                                            size="sm"
                                            onClick={toggleAllDays}
                                            disabled={selectedDays.length === ALL_DAYS.length}
                                        >
                                            Select All
                                        </BsButton>
                                    </div>
                                    {selectedDays.length === ALL_DAYS.length && (
                                        <Form.Text className="text-muted">
                                            All days selected (will be saved as "any day")
                                        </Form.Text>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                    <Card className="mt-3">
                        <Card.Body>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "25fr 25fr 50fr"
                            }}
                                className="gap-2">
                                <div className="grid-col-span-4 h5">
                                    {isDiscountValidationType ? 'Add Discount Amount' : 'Add Rate Entry'}
                                </div>

                                {!isFlatRate && <>
                                    <Input type="number"
                                        label="Stay Up To"
                                        explanation="Time limit"
                                        {...register( "upTo", { valueAsNumber: true, required: "Up To is required" } )}
                                        error={undefined} />

                                    <Select label="Time Units"
                                        {...register( "units", { required: true } )}
                                        error={undefined}>                                
                                        <option value="minutes">Minutes</option>
                                        <option value="hours"  >Hours  </option>
                                    </Select>
                                </>}

                                <MoneyInput
                                    key={inputKey}
                                    icon="$"
                                    label="Price"
                                    className={isFlatRate ? "grid-col-span-3" : ""}
                                    {...register( "price", { valueAsNumber: true, required: "Price is required" } )}
                                    error={undefined} />

                                <div className="grid-col-span-2">
                                </div>
                                
                            </div>

                            {(errors.upTo || errors.units || errors.price) && (
                                <div className="text-danger mb-2">
                                    {errors.upTo?.message || errors.units?.message || errors.price?.message}
                                </div>
                            )}

                            <div className="d-flex justify-content-end">
                                <Button type="button" className="mt-3" onClick={addRateEntry}>
                                    {isDiscountValidationType ? 'Add Discount Amount' : 'Add Rate Entry'}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                    <PageTitle className="mt-3">
                        {isDiscountValidationType ? 'Discount' : 'Rate' } Preview
                    </PageTitle>
                    <div className="text-secondary">
                        {isDiscountValidationType ? 'The discount granted for this validation' : isFlatRate ? `The flat rate for the ${vals.isEvent ? 'event' : 'validation'}` :
                         "Add rate entries to fill the duration based rate table"}
                    </div>
                    {rates.length > 0 && <SpotSyncTable className="mt-3">
                        <thead>
                            <tr>
                                {!isFlatRate && <th className="w-50 text-center">Up To </th>}
                                <th className="w-50 text-center">Rate  </th>
                                <th className="w-50 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rates.sort( ( a, b ) => a.upTo.compareTo( b.upTo ) ).map( el => <tr>
                                {!isFlatRate && <td className="text-center align-middle">
                                    {showDuration( el.upTo )}
                                </td>}
                                <td className="text-center align-middle">
                                    {showMoney( el.rate )}
                                </td>
                                <td className="text-center align-middle">
                                    <Button onClick={() => deleteRate( el )}>
                                        Delete
                                    </Button>
                                </td>
                            </tr> )}
                        </tbody>
                    </SpotSyncTable>}
                    {msg && <Alert variant="danger">
                        {msg}
                    </Alert>}
                    <div className="d-flex gap-2 my-5">
                        <Button className="w-50" type="button" variant="outline-secondary" onClick={() => nav(`/facility/${facilityId}/rate`)}>
                            Cancel
                        </Button>
                        <Button className="w-50 btn-main" type="submit" loading={isSubmitting}>
                            Submit
                        </Button>
                    </div>
                </Col>
            </Row>
        </Form>
    </Container>;
}
